/* eslint-disable jira/integration/test-id-by-folder-structure */
import React, { Fragment, memo, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import { Box, Inline, xcss } from '@atlaskit/primitives';
import { token } from '@atlaskit/tokens';
import { Tokens } from '@atlassian/jira-custom-theme-constants/src/constants.tsx';
import { IconSkeleton } from '@atlassian/jira-horizontal-nav-icons/src/ui/icon/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { EditableTitle } from './EditableTitle.tsx';

interface BaseProps {
	title?: string | null;
	withInlineEdit?: boolean;
	onConfirm?: (newTitle: string) => Promise<void>;
	onValidate?: (title: string, newTitle: string) => Promise<string | undefined>;
	id?: number;
	icon?: ReactNode | null;
	/** left-justified extra components, close to the title */
	extraComponentsClose?: ReactNode | ReactNode[] | null;
	/** right-justified extra components, far from the title */
	extraComponentsFar?: ReactNode | ReactNode[] | null;
	/** extra components, will be added to above of the title */
	extraComponentsAbove?: ReactNode | ReactNode[] | null;
	isLoading?: boolean;
	hasThemePicker?: boolean;
	testId?: string;
}

export type WithInlineEditProps = {
	withInlineEdit: true;
	onConfirm: (newTitle: string) => void;
	onValidate: (title: string, newTitle: string) => Promise<string | undefined>;
	id: number;
};

type WithoutInlineEditProps = {
	withInlineEdit?: false;
	onConfirm?: never;
	onValidate?: never;
	id?: never;
};

type Props = (WithInlineEditProps | WithoutInlineEditProps) & BaseProps;

const BaseHeader = ({
	title,
	withInlineEdit,
	onConfirm,
	onValidate,
	id,
	icon,
	extraComponentsClose,
	extraComponentsFar,
	extraComponentsAbove,
	isLoading = false,
	testId,
}: Props) => {
	const WrapperElement = extraComponentsAbove ? Box : Fragment;

	if (isLoading) {
		return (
			<WrapperElement>
				{extraComponentsAbove && (
					<Inline
						space="space.100"
						xcss={
							fg('blu-6305_tab_item_styling_in_horizontal_navigation')
								? extraComponentsAboveStylesWithNoMargin
								: extraComponentsAboveStyles
						}
					>
						<Box
							xcss={breadcrumbSkeletonStyles}
							testId="navigation-kit-header.extra-component-above-skeleton-1"
						/>
						<Box
							xcss={breadcrumbSkeletonStyles}
							testId="navigation-kit-header.extra-component-above-skeleton-2"
						/>
					</Inline>
				)}
				<Inline
					xcss={
						fg('blu-6305_tab_item_styling_in_horizontal_navigation')
							? headerWrapperStylesWithUpdatedWhitespace
							: headerWrapperStyles
					}
				>
					<IconSkeleton data-testid="business-horizontal-nav.common.ui.header.icon-skeleton" />
					<Box
						xcss={titleSkeletonStyles}
						testId="business-horizontal-nav.common.ui.header.title-skeleton"
					/>
				</Inline>
			</WrapperElement>
		);
	}

	return (
		<WrapperElement>
			{extraComponentsAbove && (
				<Inline
					xcss={
						fg('blu-6305_tab_item_styling_in_horizontal_navigation')
							? extraComponentsAboveStylesWithNoMargin
							: extraComponentsAboveStyles
					}
				>
					{extraComponentsAbove}
				</Inline>
			)}
			<Inline
				xcss={
					fg('blu-6305_tab_item_styling_in_horizontal_navigation')
						? headerWrapperStylesWithUpdatedWhitespace
						: headerWrapperStyles
				}
				testId={testId}
			>
				{icon}

				{withInlineEdit ? (
					<EditableTitle key={id} title={title} onConfirm={onConfirm} onValidate={onValidate} />
				) : (
					<Box xcss={titleContainerStyles}>
						{fg('blu-6305_tab_item_styling_in_horizontal_navigation') ? (
							<TitleWithSmallerFont>{title}</TitleWithSmallerFont>
						) : (
							<Title>{title}</Title>
						)}
					</Box>
				)}

				{extraComponentsClose && <Inline alignInline="start">{extraComponentsClose}</Inline>}

				{extraComponentsFar && (
					<Inline alignInline="end" space="space.100" xcss={extraComponentsFarStyles}>
						{extraComponentsFar}
					</Inline>
				)}
			</Inline>
		</WrapperElement>
	);
};

export default memo<Props>(BaseHeader);
export type { Props };

const headerWrapperStyles = xcss({
	justifyContent: 'flex-start',
	gap: 'space.100',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	height: token('space.400'),
});

const headerWrapperStylesWithUpdatedWhitespace = xcss({
	justifyContent: 'flex-start',
	gap: 'space.075',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	height: token('space.400'),
});

const titleSkeletonStyles = xcss({
	boxSizing: 'border-box',
	height: token('space.300'),
	width: '200px',
	backgroundColor: 'color.skeleton',
	borderRadius: 'border.radius',
});

const breadcrumbSkeletonStyles = xcss({
	boxSizing: 'border-box',
	height: token('space.300'),
	width: '90px',
	backgroundColor: 'color.skeleton',
	borderRadius: 'border.radius',
});

const extraComponentsFarStyles = xcss({
	alignSelf: 'end',
	marginLeft: 'auto',
});

const extraComponentsAboveStyles = xcss({
	justifyContent: 'flex-start',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
	marginBottom: 'space.075',
});

const extraComponentsAboveStylesWithNoMargin = xcss({
	justifyContent: 'flex-start',
	alignItems: 'center',
	minWidth: token('space.0'),
	paddingLeft: 'space.300',
	paddingRight: 'space.300',
});

const titleContainerStyles = xcss({
	display: 'grid',
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Title = styled.h1({
	marginTop: 0, // Require to override inherited styles
	// Cannot easily use color token here - var(--project-color-text) is sometimes overridden black -> white
	// See jira/src/packages/business/navigation/horizontal-nav/integration-tests/accessibility.cy-integration.tsx
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.large'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TitleWithSmallerFont = styled.h1({
	marginTop: 0, // Require to override inherited styles
	// Cannot easily use color token here - var(--project-color-text) is sometimes overridden black -> white
	// See jira/src/packages/business/navigation/horizontal-nav/integration-tests/accessibility.cy-integration.tsx
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	color: Tokens.COLOR_TEXT,
	whiteSpace: 'nowrap',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
	font: token('font.heading.medium'),
});
